import React from 'react';
import FeedbackMessage from '../../../components/FeedbackMessage/FeedbackMessage';
import { Body } from '../../../styled components/Typography';
import ListViewCards from '../cards/ListViewCards';
import ListViewTable from '../table/ListViewTable/ListViewTable';
import useListViewSetCollapse from '../../../hooks/useListViewSetCollapse';
import type {
  ListViewBooking,
  ListViewColumn,
  ListViewConfiguration,
  ListViewDiff,
  ListViewMode,
  TimeFormatSetting
} from '../../types';

interface ListViewBookingsProps {
  /* The bookings to show — already filtered and paginated; empty while the
     fetch for the selected range is still in flight or failed */
  bookings: ListViewBooking[];
  /* The bookings of the selected range are still being fetched (the initial
     load, or a range the user just picked) */
  isLoading: boolean;
  /* That fetch failed. Only this region reports it: the controls around it
     stay usable, which is what lets the user pick another range or retry */
  loadFailed: boolean;
  columns: ListViewColumn[];
  configuration: ListViewConfiguration;
  viewMode: ListViewMode;
  /* How every set on screen starts out: the toolbar's toggle, which opens or
     closes all of them at once. Each set can still be opened and closed on
     its own, so this is not handed to the view modes - it only says what the
     sets they render default to */
  showChildren: boolean;
  statusByID: ListViewDiff['statusByID'];
  now: string;
  dateFormatSetting: string;
  timeFormatSetting: TimeFormatSetting;
}

/* The bookings themselves, in the view mode the user picked. This is the only
 * place that decides which view mode is shown: a rule that overrules the
 * user's preference — a narrow screen forcing the cards, say — belongs here,
 * not in the view modes it picks between, and not in ListView.
 *
 * It also owns how the bookings' own loading and error states read, so that
 * waiting for them leaves the rest of the view — the date range, the filters,
 * the refresh controls — on screen and usable. */
const ListViewBookings = ({
  bookings,
  isLoading,
  loadFailed,
  columns,
  configuration,
  viewMode,
  showChildren,
  statusByID,
  now,
  dateFormatSetting,
  timeFormatSetting
}: ListViewBookingsProps) => {
  /* Held here rather than in either view mode, so a set the user closed in
     the table is still closed after switching to the cards */
  const setCollapse = useListViewSetCollapse(showChildren);
  if (columns.length === 0) {
    return <Body as="p">{i18n('No columns are configured for this view')}</Body>;
  }
  if (loadFailed)
    return (
      <FeedbackMessage
        variant="error"
        feedbackTexts={[i18n('The items of this view could not be loaded')]}
        dataAttributes={{ 'data-test-type': 'errorMessage' }}
      />
    );
  /* Only once they have arrived: an empty list while they are on their way is
     not "none found" */
  if (!isLoading && bookings.length === 0)
    return (
      <Body as="p">
        {i18n(
          'No items found for this day and these filters. Try another day, or change the filters.'
        )}
      </Body>
    );
  if (viewMode === 'table') {
    return (
      <ListViewTable
        bookings={bookings}
        columns={columns}
        isLoading={isLoading}
        setCollapse={setCollapse}
        statusByID={statusByID}
        now={now}
        dateFormatSetting={dateFormatSetting}
        timeFormatSetting={timeFormatSetting}
      />
    );
  }
  return (
    <ListViewCards
      bookings={bookings}
      columns={columns}
      isLoading={isLoading}
      configuration={configuration}
      setCollapse={setCollapse}
      statusByID={statusByID}
      now={now}
      dateFormatSetting={dateFormatSetting}
      timeFormatSetting={timeFormatSetting}
    />
  );
};

export default ListViewBookings;
